import type { GraphQLResult } from 'aws-amplify/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Customer } from '@skop-app/lib-domain/types'

import { getAmplifyClient } from '@/services/amplifyClient'

import {
  createCustomerMutation,
  deleteCustomerByIdMutation,
  updateCustomerMutation,
} from '../graphql'

const client = getAmplifyClient()

type upsertParams = {
  upsertType: 'create' | 'update' | 'deleteDraft' | 'delete'
  input: Customer
}

export const useUpsertClient = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['upsertClient'],
    mutationFn: upsertClient,
    onMutate(variables) {
      const { input } = variables

      queryClient.cancelQueries({
        queryKey: ['customersByOrganization', input.organizationID],
      })

      queryClient.setQueryData(
        ['customersByOrganization', input.organizationID],
        (prevData: { items: Customer[] }) => ({
          items: updateCustomerList(
            prevData?.items || [],
            variables.upsertType,
            input,
          ),
        }),
      )
      queryClient.setQueryData(['customer', input.id], input)
    },
  })
}

const upsertClient = async (variables: upsertParams) => {
  switch (variables.upsertType) {
    case 'create':
      return createCustomer(variables.input)
    case 'update':
      return updateCustomer(variables.input)
    case 'deleteDraft':
      return updateCustomer(variables.input)
    case 'delete':
      return deleteCustomer(variables.input)
    default:
      throw new Error('Invalid upsert type')
  }
}

const createCustomer = async (input: Customer) => {
  const result = (await client.graphql({
    query: createCustomerMutation,
    variables: { input },
  })) as GraphQLResult<{
    createCustomer: { createdAt: string }
  }>
  return result.data?.createCustomer
}

const updateCustomer = async (input: Customer) => {
  const result = (await client.graphql({
    query: updateCustomerMutation,
    variables: { input },
  })) as GraphQLResult<{ updateCustomer: { updatedAt: string } }>
  return result.data?.updateCustomer
}

const deleteCustomer = async ({ id }: { id: string }) => {
  const result = (await client.graphql({
    query: deleteCustomerByIdMutation,
    variables: { input: { id } },
  })) as GraphQLResult<{ deleteCustomer: { deletedAt: string } }>
  return result.data?.deleteCustomer
}

const updateCustomerList = (
  items: Customer[],
  type: upsertParams['upsertType'],
  input: Customer,
) => {
  switch (type) {
    case 'update':
      return items.map((item) =>
        item.id === input.id
          ? {
              ...input,
              createdAt: item.createdAt,
              updatedAt: new Date().toISOString(),
            }
          : item,
      )
    case 'create':
      return [...items, { ...input, createdAt: new Date().toISOString() }]
    case 'deleteDraft':
    case 'delete':
      return items.filter((item) => item.id !== input.id)
  }
}
